// frontend/src/api/legal.ts - API-Client fuer Rechtstexte und das Re-Akzept-Gate.
//
// Server-Vertrag (server/auth.js ~686-729, server/legal.js):
//   GET  /api/auth/legal-status           -> LegalStatusResponse
//   POST /api/auth/accept-pending-legal   -> LegalStatusResponse
//   POST /api/auth/postpone-legal         -> LegalStatusResponse | 409
//
// Status nur einmal beim Mount des LegalReacceptGate holen (nach Authed-Status
// aus useAuth()). Die Komponente entscheidet, wann neu geladen wird (nach
// Akzept/Postpone). Hier bewusst KEIN React-Query-Hook, damit der Fetch im
// SPA-Remount nach Logout/Login sauber zurueckgesetzt wird und keine stale
// Caches das Hard-Gate ueberleben.

import { ApiError, apiRequest } from './client';
import type { LegalStatusResponse } from './types';
import type { LegalServerSlug } from '../legal/legalSlugs';

/** Lokalisiertes HTML-Fragment fuer Dialog und Vollroute. */
export async function getLegalFragment(doc: LegalServerSlug, lang: string): Promise<string> {
  return apiRequest<string>(`/api/legal/${doc}`, {
    // Nativ erreicht das ff_lang-Cookie den API-Origin nicht. Der Header
    // uebermittelt die App-Sprache; apiRequest behandelt Auth wie gewohnt.
    headers: { 'Accept-Language': lang },
    // Auch frische Cacheeintraege revalidieren, damit ein Release neue
    // Uebersetzungen sofort zeigt statt bis zu einer Stunde alte Fragmente.
    cache: 'no-cache',
  });
}

export async function getLegalStatus(): Promise<LegalStatusResponse> {
  return apiRequest<LegalStatusResponse>('/api/auth/legal-status?presentation=server-v1', { cache: 'no-store' });
}

export async function acceptPendingLegal(): Promise<LegalStatusResponse> {
  return apiRequest<LegalStatusResponse>('/api/auth/accept-pending-legal?presentation=server-v1', {
    method: 'POST',
  });
}

export async function postponeLegal(): Promise<LegalStatusResponse> {
  return apiRequest<LegalStatusResponse>('/api/auth/postpone-legal?presentation=server-v1', {
    method: 'POST',
  });
}

/**
 * Prueft, ob ein ApiError der 409 vom Server fuer "Karenz abgelaufen, kein
 * Postpone mehr moeglich" ist. Server liefert `error: 'legal.cannot_postpone'`
 * — wir vergleichen robust gegen status + (optional) error-Token, damit ein
 * Drift im i18n-Key den Hard-Gate nicht aushebelt.
 */
export function isPostponeForbiddenError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  const data = error.data;
  if (!data || typeof data !== 'object') return true;
  const code = (data as { error?: unknown }).error;
  if (typeof code !== 'string') return true;
  return code === 'legal.cannot_postpone' || code.includes('cannot_postpone');
}
